import { useState } from "react";
import { cn } from "@/lib/utils";
import GlassCard from "./GlassCard";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useHomeLibrary } from "@/hooks/useHomeLibrary";
import { useFurniture } from "@/hooks/useFurniture";
import { useAppliances } from "@/hooks/useAppliances";
import {
    Plus,
    Zap,
    Home,
    FileText,
    MoreVertical,
    Trash2,
    Copy,
    Check,
    Loader2,
    Sofa,
    Refrigerator,
    FolderOpen,
    Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AddHomeItemSheet from "@/components/AddHomeItemSheet";
import AddFurnitureSheet from "@/components/AddFurnitureSheet";
import AddApplianceSheet from "@/components/AddApplianceSheet";
import { toast } from "@/hooks/use-toast";
import FilePreviewModal from "@/components/FilePreviewModal";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";

import { useSubscription } from "@/hooks/useSubscription";
import UpgradePlanSheet from "@/components/UpgradePlanSheet";
import { useModalHistory } from "@/hooks/useModalHistory";

const HomeLibrarySheet = ({ open, onOpenChange }) => {
    const { selectedProperty } = usePropertyContext();
    const { libraryItems, loading: libraryLoading, addLibraryItem } = useHomeLibrary(selectedProperty?.id);
    const { furniture, loading: furnitureLoading, refetch: refetchFurniture } = useFurniture(selectedProperty?.id);
    const { appliances, loading: appliancesLoading, addAppliance, refetch: refetchAppliances } = useAppliances(selectedProperty?.id);

    const { isFeatureLocked, isPropertyLocked, plan, hasPremium } = useSubscription();

    const [isAddOpen, setIsAddOpen] = useState(false);
    const [isAddFurnitureOpen, setIsAddFurnitureOpen] = useState(false);
    const [isAddApplianceOpen, setIsAddApplianceOpen] = useState(false);
    const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);

    // Back button history synchronization
    useModalHistory(open, () => onOpenChange(false), "library-sheet");
    useModalHistory(!!previewItem, () => setPreviewItem(null), "file-preview");
    useModalHistory(isAddOpen, () => setIsAddOpen(false), "add-item-sheet");
    useModalHistory(isAddApplianceOpen, () => setIsAddApplianceOpen(false), "add-appliance-sheet");
    useModalHistory(isAddFurnitureOpen, () => setIsAddFurnitureOpen(false), "add-furniture-sheet");
    useModalHistory(showUpgradeSheet, () => setShowUpgradeSheet(false), "upgrade-sheet");

    const handleAddClick = (onConfirm) => {
        const propertyLocked = isPropertyLocked(selectedProperty?.id);
        if (propertyLocked || isFeatureLocked('vault_limit', allItems.length)) {
            setShowUpgradeSheet(true);
        } else {
            onConfirm();
        }
    };

    const loading = libraryLoading || furnitureLoading || appliancesLoading;

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        toast({
            title: "Copied",
            description: "Number copied to clipboard."
        });
    };

    const handleAddAppliance = async (appliance) => {
        const success = await addAppliance({ ...appliance, propertyId: selectedProperty?.id });
        if (success) {
            refetchAppliances();
            setIsAddApplianceOpen(false);
        }
    };

    const getIcon = (category) => {
        switch (category) {
            case "Utilities": return Zap;
            case "Plans": return Home;
            case "Compliance": return FileText;
            case "Furniture": return Sofa;
            case "Appliances": return Refrigerator;
            default: return FileText;
        }
    };

    // Transform furniture to match vault item structure
    const furnitureItems = furniture.map(item => ({
        id: item.id,
        category: "Furniture",
        label: item.name,
        value: item.brand,
        file_url: item.receipt_url || item.invoice_url || item.warranty_url,
        type: 'furniture',
        ...item
    }));

    // Transform appliances to match vault item structure
    const applianceItems = appliances.map(item => ({
        id: item.id,
        category: "Appliances",
        label: `${item.brand} ${item.name}`,
        value: item.last_service_date ? `Last Service: ${new Date(item.last_service_date).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}` : "No service history",
        file_url: item.receipt_url || item.warranty_url || item.manual_url,
        type: 'appliance',
        ...item
    }));

    const allItems = [...libraryItems, ...furnitureItems, ...applianceItems];

    const sortedItemsForLimit = [...allItems].sort((a, b) => new Date(a.created_at || new Date(0)) - new Date(b.created_at || new Date(0)));

    const isItemGloballyLocked = (id) => {
        if (hasPremium) return false;
        const index = sortedItemsForLimit.findIndex(item => item.id === id);
        return index >= 5;
    };

    const groupedItems = allItems.reduce((acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
    }, {});

    const categoriesOrder = ["Utilities", "Plans", "Compliance", "Appliances", "Furniture", "Other"];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                className="max-h-[85vh] rounded-t-3xl flex flex-col pt-8"
                style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 text-left">
                    <SheetTitle className="text-xl font-bold flex items-center gap-2">
                        <FolderOpen className="w-5 h-5 text-primary" />
                        Home Library
                    </SheetTitle>
                    <SheetDescription>
                        House plans, meter numbers, invoices & documents for {selectedProperty?.name || "My Home"}.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto scrollbar-hide pb-6" style={{ overscrollBehavior: 'contain' }}>
                    <div className="space-y-6">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            </div>
                        ) : (libraryItems.length === 0 && furnitureItems.length === 0 && applianceItems.length === 0) ? (
                            <div className="text-center py-16 px-6">
                                <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FileText className="w-8 h-8 text-muted-foreground/30" />
                                </div>
                                <h2 className="text-lg font-bold text-foreground mb-1">Your Library is Empty</h2>
                                <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
                                    Store your house plans, meter numbers, and compliance certificates here for easy access.
                                </p>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button className="rounded-xl gap-2 font-bold">
                                            <Plus className="w-4 h-4" />
                                            Add First Item
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="center" className="w-56 rounded-xl p-2 mb-2">
                                        <DropdownMenuItem onClick={() => handleAddClick(() => setIsAddOpen(true))} className="gap-2 p-3 font-medium cursor-pointer rounded-lg">
                                            <FileText className="w-4 h-4 text-primary" />
                                            Upload Document (PDF / Photo)
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleAddClick(() => setIsAddApplianceOpen(true))} className="gap-2 p-3 font-medium cursor-pointer rounded-lg">
                                            <Refrigerator className="w-4 h-4 text-primary" />
                                            Add Appliance
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleAddClick(() => setIsAddFurnitureOpen(true))} className="gap-2 p-3 font-medium cursor-pointer rounded-lg">
                                            <Sofa className="w-4 h-4 text-primary" />
                                            Add Furniture
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        ) : (
                            <>
                                {categoriesOrder.map(category => {
                                    const rawItems = groupedItems[category];
                                    if (!rawItems) return null;

                                    const propertyLocked = isPropertyLocked(selectedProperty?.id);
                                    const items = [...rawItems].sort((a, b) => {
                                        const lockedA = propertyLocked || isItemGloballyLocked(a.id);
                                        const lockedB = propertyLocked || isItemGloballyLocked(b.id);
                                        if (lockedA && !lockedB) return 1;
                                        if (!lockedA && lockedB) return -1;
                                        return new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0);
                                    });

                                    return (
                                        <section key={category} className="space-y-3">
                                            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-1">
                                                {category}
                                            </h2>
                                            <div className="grid grid-cols-1 gap-2.5">
                                                {items.map((item) => {
                                                    const Icon = getIcon(item.category);
                                                    const isLocked = propertyLocked || isItemGloballyLocked(item.id);

                                                    return (
                                                        <div
                                                            key={item.id}
                                                            className="relative group active-scale transition-transform cursor-pointer"
                                                            onClick={() => {
                                                                if (isLocked) {
                                                                    setShowUpgradeSheet(true);
                                                                } else if (item.file_url) {
                                                                    setPreviewItem({ url: item.file_url, name: item.label });
                                                                } else if (item.category === "Utilities" && item.value) {
                                                                    handleCopy(item.value);
                                                                } else if (item.type === 'appliance' || item.type === 'furniture') {
                                                                    toast({
                                                                        title: "No Document",
                                                                        description: "No invoice or receipt was uploaded for this item.",
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            <GlassCard className={cn(
                                                                "p-3.5 transition-all hover:bg-white/10",
                                                                isLocked && "blur-[2px] opacity-70 grayscale pointer-events-none select-none"
                                                            )}>
                                                                <div className="flex items-center gap-3.5">
                                                                    <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
                                                                        <Icon className="w-4.5 h-4.5 text-primary" />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <h3 className="font-bold text-[14px] text-foreground truncate">
                                                                            {item.label}
                                                                        </h3>
                                                                        {item.value && (
                                                                            <p className="text-xs font-mono text-muted-foreground truncate mt-0.5">
                                                                                {item.value}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </GlassCard>

                                                            {isLocked && (
                                                                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl cursor-pointer pointer-events-auto" onClick={() => setShowUpgradeSheet(true)}>
                                                                    <div className="bg-white/90 backdrop-blur-sm p-3 rounded-xl shadow-lg border border-primary/20 flex flex-col items-center text-center scale-90">
                                                                        <div className="p-1.5 bg-primary/10 rounded-full mb-1">
                                                                            <Lock className="w-4 h-4 text-primary" />
                                                                        </div>
                                                                        <p className="font-bold text-[10px] text-foreground">Premium Item</p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    );
                                })}

                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button className="w-full rounded-2xl py-5 h-auto text-base font-bold shadow-lg shadow-primary/10 gap-2">
                                            <Plus className="w-4.5 h-4.5" />
                                            Add to Your Home
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56 rounded-xl p-2 mb-2" side="top">
                                        <DropdownMenuItem onClick={() => handleAddClick(() => setIsAddOpen(true))} className="gap-2 p-3 font-medium cursor-pointer rounded-lg">
                                            <FileText className="w-4 h-4 text-primary" />
                                            Upload Document (PDF / Photo)
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleAddClick(() => setIsAddApplianceOpen(true))} className="gap-2 p-3 font-medium cursor-pointer rounded-lg">
                                            <Refrigerator className="w-4 h-4 text-primary" />
                                            Add Appliance
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleAddClick(() => setIsAddFurnitureOpen(true))} className="gap-2 p-3 font-medium cursor-pointer rounded-lg">
                                            <Sofa className="w-4 h-4 text-primary" />
                                            Add Furniture
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        )}
                    </div>
                </div>
            </SheetContent>

            <AddHomeItemSheet
                open={isAddOpen}
                onOpenChange={setIsAddOpen}
                onSave={addLibraryItem}
            />

            <AddApplianceSheet
                open={isAddApplianceOpen}
                onOpenChange={setIsAddApplianceOpen}
                onSave={handleAddAppliance}
            />

            <AddFurnitureSheet
                open={isAddFurnitureOpen}
                onOpenChange={setIsAddFurnitureOpen}
                onSuccess={refetchFurniture}
            />

            <FilePreviewModal
                open={!!previewItem}
                onOpenChange={(open) => !open && setPreviewItem(null)}
                fileUrl={previewItem?.url}
                fileName={previewItem?.name}
            />

            <UpgradePlanSheet
                open={showUpgradeSheet}
                onOpenChange={setShowUpgradeSheet}
                currentPlan={plan}
            />
        </Sheet>
    );
};

export default HomeLibrarySheet;
